import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  pointerWithin,
  DragOverlay,
  useDroppable,
  useDndContext,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core";
import type {
  CollisionDetection,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Separator } from "@radix-ui/react-separator";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  ArrowLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  CircleCheckBig,
} from "lucide-react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Checkbox } from "./ui/checkbox";
import {
  effortMap,
  priorities,
  STATUSES,
  statuses,
  statusesColors,
  type EffortLevel,
  type Status,
} from "@/defaultData";
import { CustomSelect } from "./ClusterFilters";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { useNavigate } from "react-router";
import { Skeleton } from "./ui/skeleton";
import { getUrlTypeBadgeClassName, toUrlTypeLabel } from "@/lib/urlTypes";

export type TaskOriginalCopy = {
  title?: string | null;
  description?: string | null;
  rationale?: string | null;
  impact?: string | null;
  tasks?: any[] | null;
};

export interface Item {
  id: string;
  status: Status;
  title: string;
  description: string;
  tag: string;
  completedITems: number;
  allItems: number;
  priority: string;
  prompts: any;
  clusterId: string;
  tasks: any;
  effort: EffortLevel;
  rationale?: string;
  impact?: string;
  original?: TaskOriginalCopy | null;
  showOriginal?: boolean;
  evidence?: Record<string, any>;
  isFoundation?: boolean;
  sortIndex?: number | null;
  actionType?: string;
  sourceType?: string;
  difficulty?: string;
  checkKey?: string | null;
}

const extractGuestPostBaseDescription = (value: string) => {
  if (!value) return "";
  return value
    .split(
      /Recommended placements:|Suggested collaborator placements:|Evidence:/i,
    )[0]
    .trim();
};

const normalizeOriginalCopyValue = (value: unknown) =>
  String(value ?? "").trim();

export const hasOriginalTaskCopy = (
  item?: Pick<
    Item,
    "title" | "description" | "rationale" | "impact" | "tasks" | "original"
  > | null,
) => {
  if (!item?.original) return false;
  const original = item.original;
  const fields: Array<keyof TaskOriginalCopy> = [
    "title",
    "description",
    "rationale",
    "impact",
  ];
  if (
    fields.some((field) => {
      const originalValue = normalizeOriginalCopyValue(original[field]);
      if (!originalValue) return false;
      return originalValue !== normalizeOriginalCopyValue((item as any)[field]);
    })
  ) {
    return true;
  }
  const originalTasks = Array.isArray(original.tasks) ? original.tasks : [];
  const currentTasks = Array.isArray(item.tasks) ? item.tasks : [];
  return originalTasks.some((task, index) => {
    const current = currentTasks[index] || {};
    return (
      normalizeOriginalCopyValue(task?.title) !==
        normalizeOriginalCopyValue(current?.title) ||
      normalizeOriginalCopyValue(task?.description ?? task?.details) !==
        normalizeOriginalCopyValue(current?.description ?? current?.details)
    );
  });
};

const getDisplayTaskItem = (item: Item): Item => {
  if (!item.showOriginal || !hasOriginalTaskCopy(item) || !item.original) {
    return item;
  }
  const originalTasks = Array.isArray(item.original.tasks)
    ? item.original.tasks
    : null;
  return {
    ...item,
    title: normalizeOriginalCopyValue(item.original.title) || item.title,
    description:
      normalizeOriginalCopyValue(item.original.description) || item.description,
    rationale:
      normalizeOriginalCopyValue(item.original.rationale) ||
      item.rationale ||
      "",
    impact:
      normalizeOriginalCopyValue(item.original.impact) || item.impact || "",
    tasks: originalTasks || item.tasks,
  };
};

const normalizeGuestPostUrl = (value: unknown) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return "";
};

const extractMarkdownLink = (value: string) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (!match) return null;
  return {
    label: match[1].trim(),
    href: normalizeGuestPostUrl(match[2].trim()),
  };
};

const toDomainLabel = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return parsed.hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

const getFaviconSrc = (value?: string | null) => {
  const normalized = normalizeGuestPostUrl(value);
  if (!normalized) return "";
  return `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(
    normalized,
  )}&size=64`;
};

type GuestPostPlacement = {
  url: string;
  domainLabel: string;
  price: string;
  matchType: "Direct" | "Alternative";
  categories: string[];
  language: string;
  country: string;
};

const getGuestPostPlacements = (evidence?: Record<string, any>) => {
  const structured = Array.isArray(evidence?.guest_post_view?.placements)
    ? evidence.guest_post_view.placements
    : [];
  if (structured.length) {
    const seen = new Set<string>();
    return structured
      .map((entry: any) => {
        const url = normalizeGuestPostUrl(entry?.url || entry?.domain);
        if (!url) return null;
        const key = url.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        const matchType: "Direct" | "Alternative" =
          entry?.match_type === "alternative" ? "Alternative" : "Direct";
        const categories = Array.isArray(entry?.categories)
          ? entry.categories
              .map((item: any) => String(item || "").trim())
              .filter(Boolean)
          : [];
        const language = String(entry?.language || "").trim();
        const country = String(entry?.country || "").trim();
        const fallbackPrice =
          entry?.price_from != null
            ? `${entry?.currency || "€"}${entry?.price_from}`
            : "";
        return {
          url,
          domainLabel: toDomainLabel(url) || url,
          price:
            String(entry?.price_label || "").trim() ||
            String(fallbackPrice || "").trim() ||
            "—",
          matchType,
          categories,
          language,
          country,
        };
      })
      .filter(Boolean) as GuestPostPlacement[];
  }

  const rows: GuestPostPlacement[] = [];
  const seen = new Set<string>();
  const pushRows = (items: any[], matchType: "Direct" | "Alternative") => {
    items.forEach((match) => {
      const url = normalizeGuestPostUrl(match?.domain || match?.url);
      if (!url) return;
      const key = url.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const price =
        match?.price_from != null
          ? `${match.currency || "€"}${match.price_from}`
          : "—";
      const categories = Array.isArray(match.categories)
        ? match.categories
            .map((item: any) => String(item || "").trim())
            .filter(Boolean)
        : [];
      const language = String(match.language || "").trim();
      const country = String(match.country || "").trim();
      rows.push({
        url,
        domainLabel: toDomainLabel(url) || url,
        price,
        matchType,
        categories,
        language,
        country,
      });
    });
  };
  pushRows(evidence?.collaborator_matches?.direct_matches || [], "Direct");
  pushRows(
    evidence?.collaborator_matches?.alternative_matches || [],
    "Alternative",
  );
  return rows;
};

const getGuestPostMissingPublisherPages = (evidence?: Record<string, any>) => {
  if (Array.isArray(evidence?.guest_post_view?.missing_publisher_pages)) {
    return evidence.guest_post_view.missing_publisher_pages;
  }
  return Array.isArray(evidence?.missing_publisher_pages)
    ? evidence?.missing_publisher_pages
    : [];
};

type FoundationEvidenceRow = {
  sourceUrl: string;
  failedUrl: string;
  reason: string;
};

type ClusterEvidenceRow = {
  url: string;
  title: string;
  used: number | null;
  urlType: string | null;
  domainType:
    | "Publisher"
    | "Competitor"
    | "Community"
    | "Owned"
    | "Other"
    | null;
};

const COMMUNITY_EVIDENCE_PATTERN =
  /community|discussion|forum|thread|reddit|quora|stack\s*overflow|stackexchange|q\s*&\s*a|\bqa\b|\bqna\b/i;
const COMMUNITY_PATH_PATTERN =
  /\/(r\/[^/]+\/comments|questions?|answers?|qna|forums?|community|communities|discussion|discussions|thread|threads|topic|topics)(\/|$)/i;
const YOUTUBE_HOSTS = ["youtube.com", "youtu.be"];

const normalizeEvidenceUrlValue = (value: unknown) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return "";
};

const isYouTubeEvidenceValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalized = normalizeEvidenceUrlValue(value);
    if (!normalized) return /youtube/i.test(value);
    try {
      const host = new URL(normalized).hostname
        .replace(/^www\./i, "")
        .toLowerCase();
      return YOUTUBE_HOSTS.some(
        (youtubeHost) =>
          host === youtubeHost || host.endsWith(`.${youtubeHost}`),
      );
    } catch {
      return false;
    }
  }
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, any>;
  const urlType = String(entry?.url_type || entry?.urlType || entry?.type || "")
    .trim()
    .toLowerCase();
  if (urlType.includes("youtube")) return true;
  return isYouTubeEvidenceValue(
    entry?.url || entry?.source_url || entry?.href || entry?.loc || "",
  );
};

const isCommunityEvidenceValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalized = normalizeEvidenceUrlValue(value);
    if (!normalized) return COMMUNITY_EVIDENCE_PATTERN.test(value);
    try {
      const parsed = new URL(normalized);
      const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
      if (
        host === "reddit.com" ||
        host.endsWith(".reddit.com") ||
        host === "quora.com" ||
        host.endsWith(".quora.com") ||
        host === "stackoverflow.com" ||
        host.endsWith(".stackoverflow.com") ||
        host === "stackexchange.com" ||
        host.endsWith(".stackexchange.com")
      ) {
        return true;
      }
      if (
        host === "github.com" &&
        /\/(issues|discussions?)(\/|$)/i.test(parsed.pathname || "")
      ) {
        return true;
      }
      return COMMUNITY_PATH_PATTERN.test(parsed.pathname || "");
    } catch {
      return false;
    }
  }
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, any>;
  const domainType = String(entry?.domain_type || entry?.domainType || "")
    .trim()
    .toLowerCase();
  if (domainType.includes("community")) return true;
  const urlType = String(entry?.url_type || entry?.urlType || entry?.type || "")
    .trim()
    .toLowerCase();
  if (COMMUNITY_EVIDENCE_PATTERN.test(urlType)) return true;
  return isCommunityEvidenceValue(
    entry?.url || entry?.source_url || entry?.href || entry?.loc || "",
  );
};

const extractEvidenceEntries = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.sample)) return value.sample;
  return [];
};

const getFoundationFailedEvidenceRows = (
  evidence?: Record<string, any>,
): FoundationEvidenceRow[] => {
  if (!evidence || typeof evidence !== "object") return [];

  const rows: FoundationEvidenceRow[] = [];
  const seen = new Set<string>();

  const fallbackSitemapUrl = normalizeGuestPostUrl(
    String(evidence?.sitemap_url || evidence?.sitemap?.url || "").trim(),
  );
  const fallbackBaseUrl = normalizeGuestPostUrl(
    String(evidence?.base_url || evidence?.baseUrl || "").trim(),
  );

  const pushRow = (payload: {
    sourceUrl?: string | null;
    failedUrl?: string | null;
    reason?: string | null;
  }) => {
    const failedUrl = normalizeGuestPostUrl(payload.failedUrl || "");
    if (!failedUrl) return;
    const sourceUrl =
      normalizeGuestPostUrl(payload.sourceUrl || "") ||
      fallbackSitemapUrl ||
      fallbackBaseUrl ||
      "";
    const reason = String(payload.reason || "").trim() || "failed";
    const dedupeKey = `${sourceUrl.toLowerCase()}|${failedUrl.toLowerCase()}|${reason.toLowerCase()}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    rows.push({ sourceUrl, failedUrl, reason });
  };

  extractEvidenceEntries(evidence?.invalid_urls).forEach((entry: any) => {
    if (typeof entry === "string") {
      pushRow({
        sourceUrl: fallbackSitemapUrl,
        failedUrl: entry,
        reason: "invalid sitemap URL",
      });
      return;
    }
    pushRow({
      sourceUrl: entry?.source_url || entry?.sitemap_url || fallbackSitemapUrl,
      failedUrl: entry?.url || entry?.loc || entry?.value,
      reason: entry?.reason || "invalid sitemap URL",
    });
  });

  extractEvidenceEntries(evidence?.missing_key_urls).forEach((entry: any) => {
    if (typeof entry === "string") {
      pushRow({
        sourceUrl: fallbackSitemapUrl,
        failedUrl: entry,
        reason: "missing from sitemap key URLs",
      });
      return;
    }
    pushRow({
      sourceUrl: entry?.source_url || entry?.sitemap_url || fallbackSitemapUrl,
      failedUrl: entry?.url || entry?.loc || entry?.value,
      reason: entry?.reason || "missing from sitemap key URLs",
    });
  });

  const urlResults = extractEvidenceEntries(
    evidence?.url_results || evidence?.crawlability_urls,
  );
  urlResults.forEach((entry: any) => {
    const status = Number(entry?.status);
    const statusIsFail =
      Number.isFinite(status) && (status < 200 || status >= 300);
    const authBlocked = Boolean(entry?.auth_blocked);
    const noindex = Boolean(entry?.noindex);
    const redirects = Array.isArray(entry?.redirect_chain)
      ? entry.redirect_chain.length
      : Number(entry?.redirect_count || 0);
    const longRedirects = Number.isFinite(redirects) && redirects > 3;
    const canonicalOffDomain = entry?.canonical_on_domain === false;
    const hasFail =
      statusIsFail ||
      authBlocked ||
      noindex ||
      longRedirects ||
      canonicalOffDomain;
    if (!hasFail) return;

    const reasons: string[] = [];
    if (statusIsFail) reasons.push(`status ${status}`);
    if (authBlocked) reasons.push("auth blocked");
    if (noindex) reasons.push("noindex");
    if (longRedirects) reasons.push(`redirects ${redirects}`);
    if (canonicalOffDomain) reasons.push("canonical off-domain");
    const rawReason = String(entry?.reason || "").trim();
    if (rawReason) reasons.push(rawReason);

    pushRow({
      sourceUrl:
        entry?.source_url ||
        entry?.sitemap_url ||
        fallbackSitemapUrl ||
        fallbackBaseUrl,
      failedUrl: entry?.url || entry?.final_url || entry?.loc,
      reason: reasons.join(", "),
    });
  });

  extractEvidenceEntries(evidence?.failed_urls).forEach((entry: any) => {
    if (typeof entry === "string") {
      pushRow({
        sourceUrl: fallbackSitemapUrl || fallbackBaseUrl,
        failedUrl: entry,
        reason: "failed URL",
      });
      return;
    }
    pushRow({
      sourceUrl:
        entry?.source_url ||
        entry?.sitemap_url ||
        fallbackSitemapUrl ||
        fallbackBaseUrl,
      failedUrl: entry?.url || entry?.final_url || entry?.loc || entry?.value,
      reason: entry?.reason || "failed URL",
    });
  });

  return rows.slice(0, 12);
};

const renderMarkdownLinks = (text: string, keyPrefix = "inline") => {
  const source = String(text || "");
  const parts: React.ReactNode[] = [];
  if (!source) return parts;
  const regex =
    /(\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|~~([^~]+)~~|((https?:\/\/|www\.)[^\s)]+))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let tokenIndex = 0;

  while ((match = regex.exec(source)) !== null) {
    const [full] = match;
    const start = match.index;
    if (start > lastIndex) {
      parts.push(source.slice(lastIndex, start));
    }

    const markdownLabel = match[2];
    const markdownHref = match[3];
    const inlineCode = match[4];
    const boldAsterisk = match[5];
    const boldUnderscore = match[6];
    const italicAsterisk = match[7];
    const italicUnderscore = match[8];
    const strikeText = match[9];
    const autoUrl = match[10];

    if (markdownLabel && markdownHref) {
      const normalizedHref = normalizeGuestPostUrl(markdownHref.trim());
      if (normalizedHref) {
        parts.push(
          <a
            key={`${keyPrefix}-md-link-${start}-${tokenIndex}`}
            href={normalizedHref}
            target="_blank"
            rel="noreferrer"
            className="text-[#0A66C2] underline decoration-[#BBD7F5] underline-offset-2 hover:text-[#004182] break-all"
          >
            {markdownLabel.trim() || normalizedHref}
          </a>,
        );
      } else {
        parts.push(markdownLabel.trim());
      }
    } else if (inlineCode) {
      parts.push(
        <code
          key={`${keyPrefix}-code-${start}-${tokenIndex}`}
          className="mx-[1px] rounded bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-[12px] text-[#3F3F46]"
        >
          {inlineCode}
        </code>,
      );
    } else if (boldAsterisk || boldUnderscore) {
      parts.push(
        <strong
          key={`${keyPrefix}-bold-${start}-${tokenIndex}`}
          className="font-semibold text-[#171717]"
        >
          {(boldAsterisk || boldUnderscore || "").trim()}
        </strong>,
      );
    } else if (italicAsterisk || italicUnderscore) {
      parts.push(
        <em
          key={`${keyPrefix}-italic-${start}-${tokenIndex}`}
          className="italic text-[#525252]"
        >
          {(italicAsterisk || italicUnderscore || "").trim()}
        </em>,
      );
    } else if (strikeText) {
      parts.push(
        <span
          key={`${keyPrefix}-strike-${start}-${tokenIndex}`}
          className="line-through text-[#737373]"
        >
          {strikeText.trim()}
        </span>,
      );
    } else if (autoUrl) {
      const href = autoUrl.startsWith("http") ? autoUrl : `https://${autoUrl}`;
      parts.push(
        <a
          key={`${keyPrefix}-auto-link-${start}-${tokenIndex}`}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-[#0A66C2] underline decoration-[#BBD7F5] underline-offset-2 hover:text-[#004182] break-all"
        >
          {autoUrl}
        </a>,
      );
    } else {
      parts.push(full);
    }

    lastIndex = start + full.length;
    tokenIndex += 1;
  }

  if (lastIndex < source.length) {
    parts.push(source.slice(lastIndex));
  }

  return parts;
};

const stripDescriptionLinks = (text: string) => {
  if (!text) return "";
  let cleaned = text;
  // Remove "Link: [label](url)" or "Link: url"
  cleaned = cleaned.replace(/\bLink:\s*\[[^\]]+\]\([^)]+\)\s*/gi, "");
  cleaned = cleaned.replace(/\bLink:\s*(https?:\/\/|www\.)\S+\s*/gi, "");
  // Remove "Links:" blocks (label + following lines of URLs/domains)
  cleaned = cleaned.replace(
    /\n?Links:\s*\n(?:\s*(https?:\/\/|www\.)\S+|\s*[^\s]+\.[^\s]+)\s*(\n|$)+/gi,
    "",
  );
  // Remove remaining markdown links but keep label
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Remove bare URLs
  cleaned = cleaned.replace(/(https?:\/\/|www\.)\S+/g, "");
  return cleaned.trim();
};

const TASK_PROMPT_UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const stripTaskPromptReferences = (text: string) =>
  String(text || "")
    .replace(/\(\s*maps?\s+to\s+(?:[0-9a-f-]{36}\s*(?:,\s*)?)+\)/gi, "")
    .replace(
      /^\s*[-*•]?\s*related\s+prompts?\s*:\s*(?:[0-9a-f-]{36}\s*(?:,\s*)?)+\s*$/gim,
      "",
    )
    .replace(/\brelated\s+prompts?\s*:\s*(?:[0-9a-f-]{36}\s*(?:,\s*)?)+/gi, "")
    .replace(TASK_PROMPT_UUID_PATTERN, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const normalizeIncomingMultilineText = (text: string) =>
  String(text || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

const removeAvgPositionMeta = (text: string) =>
  String(text || "")
    .replace(/avg position:\s*[\d.]+\s*/gi, "")
    .replace(/average position:\s*[\d.]+\s*/gi, "")
    .replace(/avg position\s*[\d.]+\s*/gi, "")
    .replace(/average position\s*[\d.]+\s*/gi, "")
    .trim();

const normalizeDisplayPunctuation = (text: string) =>
  normalizeIncomingMultilineText(stripTaskPromptReferences(text))
    .replace(/([^\n])\s+(#{1,6}\s+)/g, "$1\n$2")
    .replace(/([^\n])\s+([-*]\s+)/g, "$1\n$2")
    .replace(/\s*;\s*•\s*/g, ";\n• ")
    .replace(/:\s*•\s*/g, ":\n• ")
    .replace(/\s*•\s*/g, "\n• ")
    .replace(/([^\n])\s+(\d+\.\s+)/g, "$1\n$2")
    .replace(/([.!?])\s+(?=[A-Z])/g, "$1\n")
    .replace(/,(\s*,)+/g, ", ")
    .replace(/\s+,/g, ",")
    .replace(/,\s+\./g, ".")
    .replace(/sources,\s*(citation count)/gi, "sources. $1")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const formatFoundationGeoAuditText = (text: string) =>
  stripTaskPromptReferences(String(text || ""))
    .replace(/\r\n/g, "\n")
    .replace(/\s*;\s*•\s*/g, ";\n- ")
    .replace(/\s*:\s*•\s*/g, ":\n- ")
    .replace(/\s*•\s*/g, "\n- ")
    .replace(/^\s*\*\s+/gm, "- ")
    .replace(/([.!?])\s+(?=[A-ZА-ЯЁ])/g, "$1\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const shouldHideAveragePositionMeta = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return (
    normalized.includes("outreach") ||
    normalized.includes("publisher") ||
    normalized.includes("community") ||
    normalized === "guest post"
  );
};

const isOutreachOrCommunity = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return (
    normalized.includes("outreach") ||
    normalized.includes("publisher") ||
    normalized.includes("community")
  );
};

const isOutreachTag = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return normalized.includes("outreach") || normalized.includes("publisher");
};

const isOwnedListicleTag = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return normalized === "owned listicle" || normalized === "ownedlisticle";
};

const isFreePublishTag = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return normalized === "free publish" || normalized === "freepublish";
};

const FREE_PUBLISH_DOMAINS = ["medium.com", "dev.to", "substack.com"];

const isPreferredFreePublishUrl = (value?: string | null) => {
  const domain = toDomainLabel(value) || "";
  if (!domain) return false;
  return FREE_PUBLISH_DOMAINS.some(
    (preferredDomain) =>
      domain === preferredDomain || domain.endsWith(`.${preferredDomain}`),
  );
};

const isFoundationCrawlabilityItem = (item?: Item | null) =>
  Boolean(
    item?.isFoundation &&
      String(item?.checkKey || "").toUpperCase() ===
        "CRAWLABILITY_INDEXABILITY",
  );

const isFoundationGeoAuditContentItem = (item?: Item | null) => {
  const key = String(item?.checkKey || "").toUpperCase();
  return (
    item?.isFoundation === true &&
    (key === "STRUCTURED_DATA_COMPANY_DESCRIPTION" ||
      key === "STRUCTURED_DATA_COMMERCIAL_PAGES")
  );
};

const shouldUseEnhancedTaskTextFormatting = (item?: Item | null) =>
  Boolean(
    item && item?.tag !== "Guest Post" && !isOutreachOrCommunity(item?.tag),
  );

const hasMeaningfulText = (value: unknown) => {
  const normalized = String(value ?? "").trim();
  return Boolean(normalized && !/^[-–—]+$/.test(normalized));
};

const shouldShowEvidenceUrlTypeColumn = (tag?: string | null) => {
  const normalized = String(tag || "")
    .trim()
    .toLowerCase();
  return (
    normalized === "guest post" ||
    normalized === "free publish" ||
    normalized === "freepublish"
  );
};

const shouldShowEvidenceDomainTypeColumn = (tag?: string | null) =>
  isOwnedListicleTag(tag);

const renderEvidenceUrlTypeBadge = (value?: string | null) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "-";
  return (
    <Badge className={getUrlTypeBadgeClassName(normalized)} variant="outline">
      {toUrlTypeLabel(normalized)}
    </Badge>
  );
};

const normalizeEvidenceDomainType = (
  value: unknown,
): "Publisher" | "Competitor" | "Community" | "Owned" | "Other" | null => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("competitor")) return "Competitor";
  if (normalized.includes("publisher")) return "Publisher";
  if (normalized.includes("community")) return "Community";
  if (normalized.includes("owned")) return "Owned";
  return "Other";
};

const renderEvidenceDomainTypeBadge = (
  value?: "Publisher" | "Competitor" | "Community" | "Owned" | "Other" | null,
) => {
  if (!value) return "-";
  const normalized = value;
  const className =
    normalized === "Competitor"
      ? "border-[#6E56CF] text-[#6E56CF]"
      : normalized === "Owned"
        ? "border-[#FF5E00] text-[#FF5E00]"
        : normalized === "Publisher"
          ? "border-[#3DB9CF] text-[#3DB9CF]"
          : normalized === "Community"
            ? "border-[#FFC53D] text-[#FFC53D]"
            : "border-[#E5E5E5] text-[#0A0A0A]";
  return (
    <Badge className={className} variant="outline">
      {normalized}
    </Badge>
  );
};

const extractFirstUrlFromText = (value: unknown) => {
  const raw = String(value || "");
  const match = raw.match(/https?:\/\/[^\s)]+/i);
  if (!match) return "";
  return normalizeGuestPostUrl(match[0]);
};

const normalizeMarkdownPreviewText = (value: string) =>
  String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/^\s{0,3}#{1,6}\s+(.+)$/gm, "**$1**")
    .replace(/^\s*[-*•]\s+/gm, "• ")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/gm, "")
    .replace(/\n+/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

export const normalizePlainPreviewText = (value: string) =>
  normalizeMarkdownPreviewText(value)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const splitInlineOrderedItems = (line: string): string[] => {
  const raw = String(line || "").trim();
  if (!raw) return [];
  if (!/^\d+[.)]\s+/.test(raw)) return [raw];
  const withoutFirstMarker = raw.replace(/^\d+[.)]\s+/, "");
  return withoutFirstMarker
    .split(/\s+(?=\d+[.)]\s+)/g)
    .map((segment) => segment.replace(/^\d+[.)]\s+/, "").trim())
    .filter(Boolean);
};

const normalizeMarkdownLine = (line: string) =>
  String(line || "").replace(/^[\u200B\u200C\u200D\uFEFF]+/, "");

const renderMarkdown = (
  value: string,
  options?: {
    tableLinkLabelMode?: "domain" | "full";
    preserveParagraphSpacing?: boolean;
    indentParagraphs?: boolean;
  },
) => {
  if (!value) return null;
  const tableLinkLabelMode = options?.tableLinkLabelMode ?? "domain";
  const preserveParagraphSpacing = options?.preserveParagraphSpacing ?? false;
  const indentParagraphs =
    options?.indentParagraphs ?? preserveParagraphSpacing;
  const lines = value.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let orderedListNext = 1;
  const resolveOrderedStart = (firstMarker?: number) => {
    const marker = Number.isFinite(firstMarker as number)
      ? Number(firstMarker)
      : 1;
    if (marker === 1 && orderedListNext > 1) return orderedListNext;
    return marker > 0 ? marker : orderedListNext;
  };
  while (i < lines.length) {
    const line = normalizeMarkdownLine(lines[i]);
    if (!line.trim()) {
      if (preserveParagraphSpacing && nodes.length > 0) {
        let blankCount = 1;
        i += 1;
        while (i < lines.length && !lines[i].trim()) {
          blankCount += 1;
          i += 1;
        }
        nodes.push(
          <div
            key={`gap-${i}-${blankCount}`}
            className={blankCount > 1 ? "h-1" : "h-0.5"}
          />,
        );
        continue;
      }
      i += 1;
      continue;
    }
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      /\|?\s*-{2,}\s*\|/.test(lines[i + 1])
    ) {
      const header = line
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean);
      const headerMeta = header.map((cell, index) => ({
        index,
        cell,
        normalized: cell.toLowerCase().replace(/\s+/g, ""),
      }));
      const visibleColumns = headerMeta.filter(
        (entry) => entry.normalized !== "avgposition",
      );
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|")) {
        const cells = lines[i]
          .split("|")
          .map((cell) => cell.trim())
          .filter(Boolean);
        if (cells.length) rows.push(cells);
        i += 1;
      }
      nodes.push(
        <div
          key={`table-${i}`}
          className="mt-2 overflow-x-auto rounded-md border border-[#E5E5E5]"
        >
          <Table className="table-fixed">
            <TableHeader className="bg-[#E5E5E5]">
              <TableRow>
                {visibleColumns.map((column) => {
                  const isCountColumn =
                    column.normalized === "citationcount" ||
                    column.normalized === "avgposition";
                  const headerLabel =
                    column.normalized === "citationcount"
                      ? "Used"
                      : column.cell;
                  return (
                    <TableHead
                      key={`${column.cell}-${column.index}`}
                      className={
                        isCountColumn ? "text-right w-[72px]" : "w-full"
                      }
                    >
                      {headerLabel}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={`row-${idx}`}>
                  {visibleColumns.map((column) => {
                    const cell = row[column.index] ?? "";
                    const normalizedHeader = column.normalized;
                    const isCountColumn =
                      normalizedHeader === "citationcount" ||
                      normalizedHeader === "avgposition";
                    const isLinkColumn =
                      normalizedHeader === "url" ||
                      normalizedHeader === "placement";
                    if (!isLinkColumn) {
                      return (
                        <TableCell
                          key={`${idx}-${column.index}`}
                          className={isCountColumn ? "text-right w-[72px]" : ""}
                        >
                          {renderMarkdownLinks(cell)}
                        </TableCell>
                      );
                    }

                    const markdownLink = extractMarkdownLink(cell);
                    const normalizedHref = normalizeGuestPostUrl(
                      markdownLink?.href || cell,
                    );
                    if (!normalizedHref) {
                      return (
                        <TableCell
                          key={`${idx}-${column.index}`}
                          className="max-w-0"
                        >
                          {renderMarkdownLinks(cell)}
                        </TableCell>
                      );
                    }

                    const faviconSrc = getFaviconSrc(normalizedHref);
                    const linkLabel =
                      tableLinkLabelMode === "full"
                        ? normalizedHref
                        : toDomainLabel(normalizedHref) ||
                          markdownLink?.label ||
                          normalizedHref;
                    return (
                      <TableCell key={`${idx}-${column.index}`}>
                        <div className="flex items-center gap-1 min-w-0 w-full">
                          {faviconSrc ? (
                            <img
                              className="w-[20px] h-[20px] shrink-0"
                              src={faviconSrc}
                              alt=""
                            />
                          ) : null}
                          <a
                            href={normalizedHref}
                            className="group flex items-center min-w-0 w-full"
                            target="_blank"
                            rel="noopener noreferrer"
                            title={normalizedHref}
                          >
                            <p className="min-w-0 max-w-full flex-1 truncate overflow-hidden text-ellipsis whitespace-nowrap hover:underline hover:underline-offset-2">
                              {linkLabel}
                            </p>
                            <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5] shrink-0">
                              <ArrowUpRight size={16} />
                            </p>
                          </a>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>,
      );
      continue;
    }
    if (/^\s{0,3}#{1,6}\s+/.test(line)) {
      const headingMatch = line.match(/^\s{0,3}(#{1,6})\s+(.+)$/);
      const headingLevel = headingMatch?.[1]?.length ?? 2;
      const headingText = headingMatch?.[2]?.trim() ?? line.trim();
      const headingClassName =
        headingLevel <= 2
          ? "text-sm font-semibold text-[#171717] mt-2"
          : "text-sm font-medium text-[#262626] mt-1";
      nodes.push(
        <div key={`heading-${i}`} className={headingClassName}>
          {renderMarkdownLinks(headingText)}
        </div>,
      );
      i += 1;
      continue;
    }
    if (/^\s*[-•]\s+/.test(line)) {
      const items: string[] = [];
      while (
        i < lines.length &&
        /^\s*[-•]\s+/.test(normalizeMarkdownLine(lines[i]))
      ) {
        const currentLine = normalizeMarkdownLine(lines[i]);
        items.push(currentLine.replace(/^\s*[-•]\s+/, ""));
        i += 1;
      }
      const listClassName = preserveParagraphSpacing
        ? "list-disc ml-4 pl-6 text-sm text-[#737373] leading-6 space-y-1 my-1"
        : "list-disc pl-4 text-sm text-[#737373]";
      nodes.push(
        <ul key={`list-${i}`} className={listClassName}>
          {items.map((item, idx) => (
            <li
              key={`${item}-${idx}`}
              className={preserveParagraphSpacing ? "leading-6" : undefined}
            >
              {renderMarkdownLinks(item)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      let firstMarker: number | undefined;
      while (
        i < lines.length &&
        /^\s*\d+[.)]\s+/.test(normalizeMarkdownLine(lines[i]))
      ) {
        const currentLine = normalizeMarkdownLine(lines[i]);
        const markerMatch = currentLine.match(/^\s*(\d+)[.)]\s+/);
        if (markerMatch && firstMarker === undefined) {
          firstMarker = Number(markerMatch[1]);
        }
        items.push(...splitInlineOrderedItems(currentLine));
        i += 1;
      }
      const start = resolveOrderedStart(firstMarker);
      orderedListNext = start + items.length;
      const listClassName = preserveParagraphSpacing
        ? "list-decimal ml-4 pl-6 text-sm text-[#737373] leading-6 space-y-1 my-1"
        : "list-decimal pl-4 text-sm text-[#737373]";
      nodes.push(
        <ol key={`olist-${i}`} className={listClassName} start={start}>
          {items.map((item, idx) => (
            <li
              key={`${item}-${idx}`}
              className={preserveParagraphSpacing ? "leading-6" : undefined}
            >
              {renderMarkdownLinks(item)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }
    const paragraphLines: string[] = [];
    while (i < lines.length) {
      const current = normalizeMarkdownLine(lines[i]);
      if (!current.trim()) break;
      if (/^\s{0,3}#{1,6}\s+/.test(current)) break;
      if (/^\s*[-•]\s+/.test(current)) break;
      if (/^\s*\d+[.)]\s+/.test(current)) break;
      if (
        current.includes("|") &&
        i + 1 < lines.length &&
        /\|?\s*-{2,}\s*\|/.test(lines[i + 1])
      ) {
        break;
      }
      paragraphLines.push(current.trim());
      i += 1;
    }
    const paragraphText = paragraphLines.join(" ");
    if (/^\d+[.)]\s+/.test(paragraphText.trim())) {
      const inlineOrderedItems = splitInlineOrderedItems(paragraphText);
      if (inlineOrderedItems.length) {
        const markerMatch = paragraphText.trim().match(/^(\d+)[.)]\s+/);
        const start = resolveOrderedStart(
          markerMatch ? Number(markerMatch[1]) : undefined,
        );
        orderedListNext = start + inlineOrderedItems.length;
        const listClassName = preserveParagraphSpacing
          ? "list-decimal ml-4 pl-6 text-sm text-[#737373] leading-6 space-y-1 my-1"
          : "list-decimal pl-4 text-sm text-[#737373]";
        nodes.push(
          <ol key={`olist-inline-${i}`} className={listClassName} start={start}>
            {inlineOrderedItems.map((item, idx) => (
              <li
                key={`${item}-${idx}`}
                className={preserveParagraphSpacing ? "leading-6" : undefined}
              >
                {renderMarkdownLinks(item)}
              </li>
            ))}
          </ol>,
        );
        continue;
      }
    }
    const lineClassName = "text-sm text-[#737373] leading-6";
    nodes.push(
      <div
        key={`line-${i}-${paragraphLines.length}`}
        className={lineClassName}
        style={
          preserveParagraphSpacing
            ? {
                textAlign: "justify",
                ...(indentParagraphs ? { textIndent: "1rem" } : {}),
              }
            : undefined
        }
      >
        {renderMarkdownLinks(paragraphText)}
      </div>,
    );
  }
  return nodes;
};

const Item = React.memo(function Item({
  item,
  isOverlay = false,
  onPriorityChange,
  onOriginalToggle,
}: {
  item: Item;
  isOverlay?: boolean;
  onPriorityChange?: (id: string, priority: string) => void;
  onOriginalToggle?: (id: string) => void;
}) {
  const { t } = useLocale();
  const displayItem = getDisplayTaskItem(item);
  const canShowOriginal = hasOriginalTaskCopy(item);
  const previewTitle = normalizePlainPreviewText(displayItem?.title || "");
  const previewDescription = normalizePlainPreviewText(
    displayItem?.description || "",
  );

  return (
    <div
      className={`w-full min-w-0 max-w-full p-3 flex flex-col gap-3 mb-2 bg-white border border-gray-300 rounded-md cursor-grab ${
        isOverlay ? "shadow-[0_10px_30px_rgba(0,0,0,0.2)]" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold break-words">{previewTitle}</div>
        {canShowOriginal ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 px-2 text-xs text-[#737373]"
            onClick={(event) => {
              event.stopPropagation();
              onOriginalToggle?.(item.id);
            }}
          >
            {item.showOriginal
              ? t("common.showTranslated")
              : t("common.showOriginal")}
          </Button>
        ) : null}
      </div>
      <div className="flex min-w-0 items-center justify-between gap-2">
        <Badge
          variant="secondary"
          className="min-w-0 max-w-[58%] w-auto shrink overflow-hidden"
        >
          <span className="truncate">{item?.tag}</span>
        </Badge>
        {(item?.completedITems > 0 || Math.max(item?.allItems || 0) > 0) && (
          <Badge variant="secondary" className="shrink-0">
            <CircleCheckBig strokeWidth={2} />
            {item?.completedITems || 0}/{Math.max(item?.allItems || 0)}
          </Badge>
        )}
      </div>
      <div
        className="text-[#737373] text-xs"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {previewDescription}
      </div>
      <Separator className="my-1 h-px w-full bg-border" />
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="min-w-0">
          <CustomSelect
            defaultName="All Priorities"
            defaultIconScr=""
            value={item?.priority}
            options={priorities}
            onValueChange={(value) => {
              if (value === "All Priorities") return;
              onPriorityChange?.(item.id, value);
            }}
          ></CustomSelect>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-sm font-medium capitalize whitespace-nowrap">
          <div>
            <img src={effortMap[item?.effort]} />
          </div>
          <div className="text-sm">{`${item?.effort} effort`}</div>
        </div>
      </div>
    </div>
  );
});
Item.displayName = "Item";

const DraggableItem = React.memo(function DraggableItem({
  item,
  onPriorityChange,
  onOpenItem,
  onOriginalToggle,
}: {
  item: Item;
  onOpenItem: (id: string) => void;
  onPriorityChange?: (id: string, priority: string) => void;
  onOriginalToggle?: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  };
  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="w-full min-w-0"
        {...attributes}
        {...listeners}
        onClick={() => onOpenItem(item.id)}
      >
        <Item
          item={item}
          onPriorityChange={onPriorityChange}
          onOriginalToggle={onOriginalToggle}
        />
      </div>
    </>
  );
});
DraggableItem.displayName = "DraggableItem";

const ItemSheet = React.memo(
  ({
    item,
    open,
    setOpen,
    onStatusChange,
    onPriorityChange,
    onSubtasksChange,
    onOriginalToggle,
    isSubtasksSaving,
    showBoostPrompts = true,
  }: {
    item: Item | null;
    open: boolean;
    showBoostPrompts: boolean;
    onPriorityChange: any;
    onSubtasksChange: any;
    onOriginalToggle?: (id: string) => void;
    isSubtasksSaving: boolean;
    onStatusChange?: (
      id: string | undefined,
      status: Status,
      sortIndex?: number | null,
      statusOrderIds?: string[],
      sourceOrderIds?: string[],
      sourceStatus?: Status,
    ) => void;
    setOpen: (p: boolean) => void;
  }) => {
    const { t } = useLocale();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = React.useState(false);
    const [progressValue, setProgressValue] = useState(0);
    const displayItem = item ? getDisplayTaskItem(item) : null;
    const canShowOriginal = hasOriginalTaskCopy(item);
    const hasSubtasks = (displayItem?.tasks?.length || 0) > 0;
    const prompts = item?.prompts ?? [];
    const guestPostPlacements = useMemo(
      () => getGuestPostPlacements(item?.evidence),
      [item?.evidence],
    );
    const guestPostMissingPublisherPages = useMemo(
      () => getGuestPostMissingPublisherPages(item?.evidence),
      [item?.evidence],
    );
    const guestPostMissingPublisherPagesSorted = useMemo(() => {
      return [...guestPostMissingPublisherPages].sort((a: any, b: any) => {
        const aUsedRaw = Number(a?.citation_count_in_cluster);
        const bUsedRaw = Number(b?.citation_count_in_cluster);
        const aUsed = Number.isFinite(aUsedRaw) ? aUsedRaw : -1;
        const bUsed = Number.isFinite(bUsedRaw) ? bUsedRaw : -1;
        if (bUsed !== aUsed) return bUsed - aUsed;
        const aUrl = String(a?.url || "").trim();
        const bUrl = String(b?.url || "").trim();
        return aUrl.localeCompare(bUrl);
      });
    }, [guestPostMissingPublisherPages]);
    const showEvidenceTypeColumn = useMemo(
      () => shouldShowEvidenceUrlTypeColumn(item?.tag),
      [item?.tag],
    );
    const showEvidenceDomainTypeColumn = useMemo(
      () => shouldShowEvidenceDomainTypeColumn(item?.tag),
      [item?.tag],
    );
    const foundationFailedEvidenceRows = useMemo(
      () =>
        item?.isFoundation
          ? getFoundationFailedEvidenceRows(item?.evidence)
          : [],
      [item?.evidence, item?.isFoundation],
    );
    const outreachCommunityEvidenceRows = useMemo<ClusterEvidenceRow[]>(() => {
      if (item?.isFoundation) return [];
      const isCommunityTask = COMMUNITY_EVIDENCE_PATTERN.test(
        `${item?.tag || ""} ${displayItem?.title || ""} ${displayItem?.description || ""}`,
      );
      const isYouTubeTask = /\byoutube\b/i.test(
        `${item?.tag || ""} ${displayItem?.title || ""} ${displayItem?.description || ""}`,
      );
      const applyPostFilters = (inputRows: ClusterEvidenceRow[]) => {
        let filteredRows = inputRows;
        if (isCommunityTask) {
          filteredRows = filteredRows.filter((entry) =>
            isCommunityEvidenceValue({
              domain_type: entry.domainType,
              url_type: entry.urlType,
              url: entry.url,
            }),
          );
        }
        if (isYouTubeTask) {
          filteredRows = filteredRows.filter((entry) =>
            isYouTubeEvidenceValue({
              domain_type: entry.domainType,
              url_type: entry.urlType,
              url: entry.url,
            }),
          );
        }
        if (isOutreachTag(item?.tag)) {
          filteredRows = filteredRows.filter((entry) => {
            if (entry.domainType === "Competitor") return false;
            const normalizedType = String(entry.urlType || "")
              .trim()
              .toLowerCase();
            return (
              normalizedType !== "product_page" &&
              normalizedType !== "productpage" &&
              normalizedType !== "product page"
            );
          });
        }
        if (isFreePublishTag(item?.tag)) {
          filteredRows = filteredRows.filter((entry) =>
            isPreferredFreePublishUrl(entry.url),
          );
        }

        return filteredRows
          .slice()
          .sort((a, b) => {
            const aUsed = a.used ?? -1;
            const bUsed = b.used ?? -1;
            if (bUsed !== aUsed) return bUsed - aUsed;
            return a.url.localeCompare(b.url);
          })
          .slice(0, 12);
      };
      const rows: ClusterEvidenceRow[] = [];
      const rowIndexByKey = new Map<string, number>();
      const toUsedCount = (value: any): number | null => {
        const raw =
          value?.citation_count_in_cluster ??
          value?.citationCountInCluster ??
          value?.used ??
          value?.count;
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      };
      const toUrlType = (value: any): string | null => {
        const raw = String(value?.url_type || value?.urlType || "").trim();
        if (!raw) return null;
        return raw.toLowerCase();
      };
      const toDomainType = (
        value: any,
      ): "Publisher" | "Competitor" | "Community" | "Owned" | "Other" | null =>
        normalizeEvidenceDomainType(value?.domain_type || value?.domainType);
      const pushRow = (
        value: unknown,
        title?: string,
        used?: number | null,
        urlType?: string | null,
        domainType?:
          | "Publisher"
          | "Competitor"
          | "Community"
          | "Owned"
          | "Other"
          | null,
      ) => {
        const url = normalizeGuestPostUrl(value);
        if (!url) return;
        const key = url.toLowerCase();
        const normalizedTitle = String(title || "").trim();
        const nextUsed =
          typeof used === "number" && Number.isFinite(used) ? used : null;
        const nextUrlType =
          String(urlType || "")
            .trim()
            .toLowerCase() || null;
        const nextDomainType = domainType || null;
        const existingIndex = rowIndexByKey.get(key);
        if (existingIndex != null) {
          const existing = rows[existingIndex];
          if (!existing.title && normalizedTitle) {
            existing.title = normalizedTitle;
          }
          if (nextUsed !== null) {
            existing.used =
              existing.used === null
                ? nextUsed
                : Math.max(existing.used, nextUsed);
          }
          if (!existing.urlType && nextUrlType) {
            existing.urlType = nextUrlType;
          }
          if (!existing.domainType && nextDomainType) {
            existing.domainType = nextDomainType;
          }
          return;
        }
        rowIndexByKey.set(key, rows.length);
        rows.push({
          url,
          title: normalizedTitle,
          used: nextUsed,
          urlType: nextUrlType,
          domainType: nextDomainType,
        });
      };

      const sourceUrls = [
        ...(Array.isArray(item?.evidence?.source_urls)
          ? item.evidence.source_urls
          : []),
        ...(Array.isArray(item?.evidence?.community_urls)
          ? item.evidence.community_urls
          : []),
        ...(Array.isArray(item?.evidence?.missing_publisher_pages)
          ? item.evidence.missing_publisher_pages
          : []),
        ...(Array.isArray(item?.evidence?.missing_community_pages)
          ? item.evidence.missing_community_pages
          : []),
        ...(Array.isArray(item?.evidence?.publisher_urls)
          ? item.evidence.publisher_urls
          : []),
        ...(Array.isArray(item?.evidence?.outreach_urls)
          ? item.evidence.outreach_urls
          : []),
        ...(Array.isArray(item?.evidence?.urls) ? item.evidence.urls : []),
      ];
      const fallbackSourceRows = Array.isArray(
        item?.evidence?.fallback_source_rows,
      )
        ? item.evidence.fallback_source_rows
        : [];
      const isCommunityEvidenceEntry = (value: any) => {
        return isCommunityEvidenceValue(value);
      };
      const isYouTubeEvidenceEntry = (value: any) => {
        return isYouTubeEvidenceValue(value);
      };
      sourceUrls.forEach((value: any) => {
        if (isCommunityTask && !isCommunityEvidenceEntry(value)) return;
        if (isYouTubeTask && !isYouTubeEvidenceEntry(value)) return;
        if (typeof value === "string") {
          pushRow(value);
          return;
        }
        pushRow(
          value?.url || value?.source_url || value?.href || value?.loc,
          value?.title,
          toUsedCount(value),
          toUrlType(value),
          toDomainType(value),
        );
      });
      if (
        !rows.length &&
        !isFreePublishTag(item?.tag) &&
        !isCommunityTask &&
        !isYouTubeTask
      ) {
        const subtasks = Array.isArray(displayItem?.tasks)
          ? displayItem.tasks
          : [];
        subtasks.forEach((task: any) => {
          pushRow(task?.url, task?.title);
          if (!task?.url) {
            pushRow(
              extractFirstUrlFromText(task?.description || task?.details),
              task?.title,
            );
          }
        });
      }
      if (
        !rows.length &&
        isOutreachOrCommunity(item?.tag) &&
        !isYouTubeTask &&
        fallbackSourceRows.length
      ) {
        const communityCandidates = fallbackSourceRows.filter((value: any) => {
          return isCommunityEvidenceEntry(value);
        });
        const rowsToUse = isCommunityTask
          ? communityCandidates
          : fallbackSourceRows;
        rowsToUse.forEach((value: any) => {
          if (typeof value === "string") {
            pushRow(value);
            return;
          }
          pushRow(
            value?.url || value?.source_url || value?.href || value?.loc,
            value?.title,
            toUsedCount(value),
            toUrlType(value),
            toDomainType(value),
          );
        });
      }

      return applyPostFilters(rows);
    }, [
      displayItem?.description,
      displayItem?.tasks,
      displayItem?.title,
      item?.isFoundation,
      item?.evidence,
      item?.tag,
    ]);
    const outreachCommunityEvidenceText = useMemo(() => {
      if (!isOutreachOrCommunity(item?.tag)) return "";
      const normalized = String(item?.evidence?.text || "").trim();
      return hasMeaningfulText(normalized) ? normalized : "";
    }, [item?.tag, item?.evidence]);
    const rationaleText = useMemo(() => {
      const normalized = String(displayItem?.rationale || "").trim();
      return hasMeaningfulText(normalized) ? normalized : "";
    }, [displayItem?.rationale]);
    const hasRationaleText = useMemo(
      () => Boolean(rationaleText),
      [rationaleText],
    );
    const shouldRenderRationaleEvidenceSection = useMemo(() => {
      if (isFoundationGeoAuditContentItem(item)) {
        return false;
      }
      if (item?.isFoundation) {
        return hasRationaleText || foundationFailedEvidenceRows.length > 0;
      }
      if (isOutreachOrCommunity(item?.tag)) {
        return (
          Boolean(outreachCommunityEvidenceText) ||
          outreachCommunityEvidenceRows.length > 0
        );
      }
      return hasRationaleText || outreachCommunityEvidenceRows.length > 0;
    }, [
      item?.tag,
      item?.isFoundation,
      hasRationaleText,
      foundationFailedEvidenceRows.length,
      outreachCommunityEvidenceText,
      outreachCommunityEvidenceRows.length,
    ]);
    const hideFreePublishEvidenceBlock = useMemo(
      () =>
        isFreePublishTag(item?.tag) &&
        outreachCommunityEvidenceRows.length === 0,
      [item?.tag, outreachCommunityEvidenceRows.length],
    );
    const showRationaleEvidenceAsEvidenceTitle = useMemo(
      () =>
        !hideFreePublishEvidenceBlock &&
        (item?.isFoundation ||
          shouldHideAveragePositionMeta(item?.tag) ||
          outreachCommunityEvidenceRows.length > 0),
      [
        hideFreePublishEvidenceBlock,
        item?.isFoundation,
        item?.tag,
        outreachCommunityEvidenceRows.length,
      ],
    );
    useEffect(() => {
      if (!isSubtasksSaving) {
        const totalTasks = displayItem?.tasks?.length || 0;
        const completedTasks =
          displayItem?.tasks?.filter((task: any) => task.done).length || 0;
        setProgressValue(
          totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0,
        );
      }
    }, [displayItem?.tasks, isSubtasksSaving]);

    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-4xl lg:max-w-5xl p-3 overflow-x-auto">
          <div className="flex justify-between bg-white z-10">
            <div className="flex gap-2 items-center">
              <SheetClose asChild>
                <Button
                  onClick={() => setOpen(false)}
                  variant="outline"
                  className="cursor-pointer"
                >
                  <ArrowLeft />
                  <LocalizedText id="legacy.back.yjmdam" />
                </Button>
              </SheetClose>
              <Separator orientation="vertical" />
              <div className="font-semibold text-base text-[#0A0A0A]">
                {renderMarkdownLinks(
                  normalizeMarkdownPreviewText(displayItem?.title || ""),
                  `sheet-title-${item?.id || "item"}`,
                )}
              </div>
              {item && canShowOriginal ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-[#737373]"
                  onClick={() => onOriginalToggle?.(item.id)}
                >
                  {item.showOriginal
                    ? t("common.showTranslated")
                    : t("common.showOriginal")}
                </Button>
              ) : null}
            </div>
          </div>
          <SheetHeader className="flex p-2 flex-row justify-between border rounded-lg  ">
            <SheetTitle className="sr-only">
              <LocalizedText id="legacy.details.1uhd7mv" />
            </SheetTitle>
            <div className="flex w-full justify-between">
              <div className="flex items-center ml-4 flex gap-2 mr-3 text-muted-foreground text-sm font-normal">
                <div className="text-sm">
                  <LocalizedText id="legacy.status.1iwhbo1" />
                </div>

                <Select
                  value={item?.status}
                  onValueChange={(value) =>
                    onStatusChange?.(item?.id, value as Status)
                  }
                >
                  <SelectTrigger className="w-fit px-1 py-1">
                    <SelectValue
                      placeholder={t("legacy.change.status.b7twl3")}
                      className="flex items-center gap-2"
                    >
                      <div className="flex">
                        {
                          statuses.find((opt) => opt.label === item?.status)
                            ?.icon
                        }
                        {
                          statuses.find((opt) => opt.label === item?.status)
                            ?.label
                        }
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((option) => (
                      <SelectItem key={option.value} value={option.label}>
                        <div className="flex items-center gap-2">
                          {option.icon}
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator
                orientation="vertical"
                className="
    bg-[#E5E5E5]
    data-[orientation=vertical]:h-[30px]
    data-[orientation=vertical]:w-px
  "
              />
              <div className="flex items-center ml-4 flex gap-2 mr-3 text-muted-foreground text-sm font-normal">
                <div className="text-sm">
                  <LocalizedText id="legacy.proprity.1drzymm" />
                </div>
                <CustomSelect
                  defaultName="All Priorities"
                  defaultIconScr=""
                  value={item?.priority}
                  options={priorities}
                  onValueChange={(value) => {
                    if (value === "All Priorities") return;
                    onPriorityChange?.(item?.id, value);
                  }}
                ></CustomSelect>
              </div>
              <Separator
                orientation="vertical"
                className="
    bg-[#E5E5E5]
    data-[orientation=vertical]:h-[30px]
    data-[orientation=vertical]:w-px
  "
              />
              <div className="flex items-center ml-4 flex gap-2 mr-3 text-muted-foreground text-sm font-normal">
                <div className="text-sm">
                  <LocalizedText id="legacy.effort.12l096x" />
                </div>
                <Badge variant="outline" className="h-full">
                  <div>
                    {item?.effort && <img src={effortMap[item?.effort]} />}
                  </div>
                  <div className="capitalize">{`${item?.effort}`}</div>
                </Badge>
              </div>
            </div>
          </SheetHeader>
          <div>
            <div className="flex flex-col gap-2 border-t border-x rounded-t-md h-fit">
              {prompts.length > 0 && showBoostPrompts && (
                <div className="relative flex-col flex justify-end gap-2">
                  <div className="p-2 pb-1 text-sm font-medium">
                    <LocalizedText id="legacy.boost.prompts.1u2v6el" />{" "}
                  </div>
                  <div className="text-sm p-2 border-b w-full gap-2 ">
                    <Collapsible
                      open={isOpen}
                      onOpenChange={setIsOpen}
                      className=""
                    >
                      <div className="max-h-[101px] mb-[20px] overflow-x-auto flex flex-row gap-2 flex-wrap">
                        {item?.prompts.slice(0, 4).length ? (
                          item?.prompts
                            .slice(0, 4)
                            .map((prompt: any, index: number) => {
                              return (
                                <div
                                  key={`${prompt.title}-${index}`}
                                  onClick={() => {
                                    const params = new URLSearchParams(
                                      window.location.search,
                                    );
                                    const brand = params.get("brand");
                                    const brandId = brand
                                      ? `?brandId=${brand}`
                                      : "";
                                    const clusterId = brandId
                                      ? `&cluster=${item.clusterId}`
                                      : `?cluster=${item.clusterId}`;

                                    navigate(
                                      `/dashboard/prompts${brandId}${clusterId}`,
                                    );
                                  }}
                                  className="cursor-pointer flex items-center w-fit gap-1 bg-[#F5F5F5] text-[#737373] rounded-md p-1"
                                >
                                  <div className=" underline decoration-dotted underline-offset-4 truncate max-w-[240px]">
                                    {prompt.title}
                                  </div>
                                  <ArrowUpRight size={14} />
                                </div>
                              );
                            })
                        ) : (
                          <div>-</div>
                        )}
                        {item?.prompts.length > 4 && (
                          <>
                            <CollapsibleContent className="flex flex-row gap-2 flex-wrap">
                              {item?.prompts
                                .slice(4)
                                .map((prompt: any, index: number) => {
                                  return (
                                    <div
                                      key={`${prompt.title}-${index + 4}`}
                                      onClick={() => {
                                        const params = new URLSearchParams(
                                          window.location.search,
                                        );
                                        const brand = params.get("brand");
                                        const brandId = brand
                                          ? `?brandId=${brand}`
                                          : "";
                                        navigate(
                                          `/dashboard/prompts${brandId}`,
                                        );
                                      }}
                                      className="flex items-center w-fit gap-1 bg-[#F5F5F5] text-[#737373] rounded-md p-1"
                                    >
                                      <div className="cursor-pointer underline decoration-dotted underline-offset-4 truncate max-w-[240px]">
                                        {prompt.title}
                                      </div>
                                      <ArrowUpRight size={14} />
                                    </div>
                                  );
                                })}
                            </CollapsibleContent>
                            <div className="flex absolute -bottom-[16px] left-1/2 -translate-x-1/2 items-center justify-between gap-4 px-4">
                              <CollapsibleTrigger asChild>
                                <Button variant="outline" className="flex">
                                  {isOpen ? (
                                    <ChevronUp></ChevronUp>
                                  ) : (
                                    <ChevronDown></ChevronDown>
                                  )}{" "}
                                  <LocalizedText id="legacy.show.yjzr6u" />{" "}
                                  {isOpen ? "less" : "more"}
                                </Button>
                              </CollapsibleTrigger>
                            </div>
                          </>
                        )}
                      </div>
                    </Collapsible>
                  </div>
                </div>
              )}
              <div className="flex flex-col text-sm p-2 font-medium flex gap-2">
                <LocalizedText id="legacy.description.r2fl5v" />
                <div className="text-[#737373] font-normal">
                  {item?.tag === "Guest Post"
                    ? renderMarkdown(
                        stripDescriptionLinks(
                          extractGuestPostBaseDescription(
                            displayItem?.description || "",
                          ),
                        ),
                      )
                    : renderMarkdown(
                        isFoundationGeoAuditContentItem(item)
                          ? formatFoundationGeoAuditText(
                              String(displayItem?.description || ""),
                            )
                          : normalizeDisplayPunctuation(
                              stripDescriptionLinks(
                                displayItem?.description || "",
                              ),
                            ),
                        {
                          tableLinkLabelMode: isFoundationGeoAuditContentItem(
                            item,
                          )
                            ? "full"
                            : "domain",
                          preserveParagraphSpacing:
                            shouldUseEnhancedTaskTextFormatting(item),
                        },
                      )}
                </div>
              </div>
            </div>

            <div className="border p-2 rounded-b-md">
              {hasSubtasks && (
                <>
                  <div className="flex gap-6 items-center justify-between">
                    <div className="my-2 text-sm font-medium">
                      <LocalizedText id="legacy.subtasks.s9xllr" />
                    </div>
                    <div className="w-full flex items-center gap-2">
                      <div className="text-[#737373] text-sm">
                        {progressValue}%
                      </div>
                      <Progress
                        className="bg-[#ffcdb1] relative h-2 overflow-hidden rounded-full"
                        value={progressValue}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 flex-col">
                    {displayItem?.tasks.map((task: any, index: number) => {
                      return (
                        <div
                          className={`flex items-start gap-2 p-2 border rounded-md ${task.done ? "border-orange-500" : "border-gray-300"}`}
                        >
                          <Checkbox
                            checked={task.done}
                            onCheckedChange={(value) => {
                              if (isSubtasksSaving) return;
                              if (!item) return;
                              const nextTasks = item.tasks.map(
                                (entry: any, i: number) =>
                                  i === index
                                    ? { ...entry, done: Boolean(value) }
                                    : entry,
                              );
                              onSubtasksChange?.(item.id, nextTasks);
                            }}
                            disabled={isSubtasksSaving}
                            className="mt-[3px] cursor-pointer data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 data-[state=checked]:text-white"
                          />
                          <div>
                            <div
                              className={`font-medium text-sm ${task.done ? "line-through decoration-1" : ""}`}
                            >
                              {renderMarkdownLinks(
                                normalizeMarkdownPreviewText(task.title || ""),
                                `subtask-title-${item?.id || "item"}-${index}`,
                              )}
                            </div>
                            {task.description ? (
                              <div
                                className={`text-[#737373] text-sm whitespace-pre-line ${task.done ? "line-through decoration-1" : ""}`}
                              >
                                {renderMarkdown(
                                  isFoundationGeoAuditContentItem(item)
                                    ? formatFoundationGeoAuditText(
                                        String(task.description || ""),
                                      )
                                    : normalizeDisplayPunctuation(
                                        removeAvgPositionMeta(
                                          item?.isFoundation
                                            ? String(task.description || "")
                                            : stripDescriptionLinks(
                                                task.description,
                                              ),
                                        ),
                                      ),
                                  {
                                    tableLinkLabelMode:
                                      isFoundationGeoAuditContentItem(item)
                                        ? "full"
                                        : "domain",
                                    preserveParagraphSpacing:
                                      shouldUseEnhancedTaskTextFormatting(item),
                                    indentParagraphs: false,
                                  },
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {item?.tag === "Guest Post" && item?.evidence && (
                <div className="flex flex-col text-sm p-2 gap-3">
                  {guestPostPlacements.length > 0 && (
                    <div>
                      <div className="text-sm font-medium">
                        <LocalizedText id="legacy.recommended.placements.from.collaborator.pro.xvbiq2" />
                      </div>
                      <div className="mt-2">
                        {(() => {
                          const displayedPlacements = guestPostPlacements.slice(
                            0,
                            8,
                          );
                          const isOddCount =
                            displayedPlacements.length % 2 === 1;
                          return (
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:auto-rows-fr">
                              {displayedPlacements.map((placement, index) => {
                                const faviconSrc = getFaviconSrc(placement.url);
                                const placementNotes = placement.categories
                                  .length
                                  ? placement.categories.slice(0, 4).join(", ")
                                  : "No category notes";
                                const isLastOddCard =
                                  isOddCount &&
                                  index === displayedPlacements.length - 1;
                                return (
                                  <div
                                    key={`${placement.url}-${placement.matchType}`}
                                    className={`h-full rounded-md border border-[#E5E5E5] bg-white p-3 flex flex-col ${
                                      isLastOddCard ? "md:col-span-2" : ""
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0 flex items-center gap-1">
                                        {faviconSrc ? (
                                          <img
                                            className="w-[24px] h-[24px]"
                                            src={faviconSrc}
                                            alt=""
                                          />
                                        ) : null}
                                        <a
                                          href={placement.url}
                                          className="group flex items-center"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={placement.url}
                                        >
                                          <p className="max-w-[220px] hover:underline hover:underline-offset-2 truncate overflow-hidden text-ellipsis whitespace-nowrap">
                                            {placement.domainLabel}
                                          </p>
                                          <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5]">
                                            <ArrowUpRight size={16} />
                                          </p>
                                        </a>
                                      </div>
                                      <div className="shrink-0 rounded-full bg-[#FFF1E8] px-2 py-0.5 text-xs font-semibold text-[#A63E00]">
                                        {placement.price}
                                      </div>
                                    </div>
                                    <div className="mt-2 text-xs text-[#737373] flex-1">
                                      {placementNotes}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                                      <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                        {placement.matchType}
                                      </span>
                                      {placement.country ? (
                                        <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                          {placement.country}
                                        </span>
                                      ) : null}
                                      {placement.language ? (
                                        <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                          {placement.language}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {guestPostMissingPublisherPages.length > 0 && (
                    <div>
                      <div className="text-sm font-medium">
                        <LocalizedText id="legacy.evidence.publisher.pages.missing.brand.9wq8al" />
                      </div>
                      <div className="mt-2">
                        <Table className="table-fixed">
                          <TableHeader className="bg-[#E5E5E5]">
                            <TableRow>
                              <TableHead className="w-full">URL</TableHead>
                              {showEvidenceTypeColumn && (
                                <TableHead className="w-[140px]">
                                  <LocalizedText id="legacy.url.type.1i7au46" />
                                </TableHead>
                              )}
                              <TableHead className="text-right w-[72px]">
                                <LocalizedText id="legacy.used.yjrj1e" />
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {guestPostMissingPublisherPagesSorted.map(
                              (entry: any, index: number) => {
                                const url = String(entry?.url || "").trim();
                                if (!url) return null;
                                const faviconSrc = getFaviconSrc(url);
                                const citedCountRaw = Number(
                                  entry?.citation_count_in_cluster,
                                );
                                const citedCount = Number.isFinite(
                                  citedCountRaw,
                                )
                                  ? citedCountRaw
                                  : null;
                                return (
                                  <TableRow key={`${url}-${index}`}>
                                    <TableCell className="w-full max-w-0">
                                      <div className="flex items-center gap-1 min-w-0 w-full">
                                        {faviconSrc ? (
                                          <img
                                            className="w-[24px] h-[24px] shrink-0"
                                            src={faviconSrc}
                                            alt=""
                                          />
                                        ) : null}
                                        <a
                                          href={url}
                                          className="group flex items-center min-w-0 w-full"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={url}
                                        >
                                          <p className="min-w-0 max-w-full flex-1 hover:underline hover:underline-offset-2 truncate overflow-hidden text-ellipsis whitespace-nowrap">
                                            {url}
                                          </p>
                                          <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5] shrink-0">
                                            <ArrowUpRight size={16} />
                                          </p>
                                        </a>
                                      </div>
                                    </TableCell>
                                    {showEvidenceTypeColumn ? (
                                      <TableCell>
                                        {renderEvidenceUrlTypeBadge(
                                          String(
                                            entry?.url_type ||
                                              entry?.urlType ||
                                              "",
                                          )
                                            .trim()
                                            .toLowerCase(),
                                        )}
                                      </TableCell>
                                    ) : null}
                                    <TableCell className="text-right">
                                      {citedCount !== null ? citedCount : "-"}
                                    </TableCell>
                                  </TableRow>
                                );
                              },
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {shouldRenderRationaleEvidenceSection && (
                <div className="flex font-medium flex-col text-sm p-2 flex gap-2">
                  {item?.isFoundation || showRationaleEvidenceAsEvidenceTitle
                    ? "Evidence"
                    : "Rationale"}
                  {!isFoundationCrawlabilityItem(item) ? (
                    isOutreachOrCommunity(item?.tag) ? (
                      outreachCommunityEvidenceText ? (
                        <div className="text-[#737373] font-normal">
                          {renderMarkdown(
                            normalizeDisplayPunctuation(
                              outreachCommunityEvidenceText,
                            ),
                            { tableLinkLabelMode: "full" },
                          )}
                        </div>
                      ) : null
                    ) : rationaleText ? (
                      <div className="text-[#737373] font-normal">
                        {renderMarkdown(
                          normalizeDisplayPunctuation(
                            shouldHideAveragePositionMeta(item?.tag)
                              ? removeAvgPositionMeta(rationaleText)
                              : rationaleText,
                          ),
                          {
                            tableLinkLabelMode: shouldHideAveragePositionMeta(
                              item?.tag,
                            )
                              ? "full"
                              : "domain",
                            preserveParagraphSpacing:
                              shouldUseEnhancedTaskTextFormatting(item),
                          },
                        )}
                      </div>
                    ) : null
                  ) : null}
                  {!item?.isFoundation &&
                    item?.tag !== "Guest Post" &&
                    !hideFreePublishEvidenceBlock &&
                    outreachCommunityEvidenceRows.length > 0 && (
                      <div className="mt-2">
                        <Table className="table-fixed">
                          <TableHeader className="bg-[#E5E5E5]">
                            <TableRow>
                              <TableHead className="w-full">URL</TableHead>
                              {showEvidenceTypeColumn && (
                                <TableHead className="w-[140px]">
                                  <LocalizedText id="legacy.url.type.1i7au46" />
                                </TableHead>
                              )}
                              {showEvidenceDomainTypeColumn && (
                                <TableHead className="w-[140px]">
                                  <LocalizedText id="legacy.domain.type.l5hh5p" />
                                </TableHead>
                              )}
                              <TableHead className="text-right w-[72px]">
                                <LocalizedText id="legacy.used.yjrj1e" />
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {outreachCommunityEvidenceRows.map(
                              (entry, index) => {
                                const faviconSrc = getFaviconSrc(entry.url);
                                return (
                                  <TableRow key={`${entry.url}-${index}`}>
                                    <TableCell className="w-full max-w-0">
                                      <div className="flex items-center gap-1 min-w-0 w-full">
                                        {faviconSrc ? (
                                          <img
                                            className="w-[24px] h-[24px] shrink-0"
                                            src={faviconSrc}
                                            alt=""
                                          />
                                        ) : null}
                                        <a
                                          href={entry.url}
                                          className="group flex items-center min-w-0 w-full"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={entry.url}
                                        >
                                          <p className="min-w-0 max-w-full flex-1 hover:underline hover:underline-offset-2 truncate overflow-hidden text-ellipsis whitespace-nowrap">
                                            {entry.url}
                                          </p>
                                          <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5] shrink-0">
                                            <ArrowUpRight size={16} />
                                          </p>
                                        </a>
                                      </div>
                                    </TableCell>
                                    {showEvidenceTypeColumn ? (
                                      <TableCell>
                                        {renderEvidenceUrlTypeBadge(
                                          entry.urlType,
                                        )}
                                      </TableCell>
                                    ) : null}
                                    {showEvidenceDomainTypeColumn ? (
                                      <TableCell>
                                        {renderEvidenceDomainTypeBadge(
                                          entry.domainType,
                                        )}
                                      </TableCell>
                                    ) : null}
                                    <TableCell className="text-right">
                                      {entry.used !== null ? entry.used : "-"}
                                    </TableCell>
                                  </TableRow>
                                );
                              },
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  {item?.isFoundation &&
                    foundationFailedEvidenceRows.length > 0 && (
                      <div className="mt-2 overflow-x-auto rounded-md border border-[#E5E5E5]">
                        <Table className="table-fixed">
                          <TableHeader className="bg-[#E5E5E5]">
                            <TableRow>
                              <TableHead className="w-full">URL</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {foundationFailedEvidenceRows.map((row, index) => {
                              const failedFavicon = getFaviconSrc(
                                row.failedUrl,
                              );
                              return (
                                <TableRow
                                  key={`${row.sourceUrl}-${row.failedUrl}-${index}`}
                                >
                                  <TableCell className="max-w-0">
                                    <div className="flex items-center gap-1 min-w-0 w-full">
                                      {failedFavicon ? (
                                        <img
                                          className="w-[20px] h-[20px] shrink-0"
                                          src={failedFavicon}
                                          alt=""
                                        />
                                      ) : null}
                                      <a
                                        href={row.failedUrl}
                                        className="group flex items-center min-w-0 w-full"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={row.failedUrl}
                                      >
                                        <p className="min-w-0 max-w-full flex-1 truncate overflow-hidden text-ellipsis whitespace-nowrap hover:underline hover:underline-offset-2">
                                          {row.failedUrl}
                                        </p>
                                        <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5] shrink-0">
                                          <ArrowUpRight size={16} />
                                        </p>
                                      </a>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                </div>
              )}
              <div className="flex flex-col text-sm p-2 flex gap-2 font-medium">
                <LocalizedText id="legacy.impact.1a2cqzr" />
                {displayItem?.impact ? (
                  <div className="text-[#737373] font-normal">
                    {renderMarkdown(
                      isFoundationGeoAuditContentItem(item)
                        ? formatFoundationGeoAuditText(
                            String(displayItem.impact || ""),
                          )
                        : normalizeDisplayPunctuation(displayItem.impact),
                      {
                        tableLinkLabelMode: isFoundationGeoAuditContentItem(
                          item,
                        )
                          ? "full"
                          : "domain",
                        preserveParagraphSpacing:
                          shouldUseEnhancedTaskTextFormatting(item),
                      },
                    )}
                  </div>
                ) : (
                  <div className="text-[#737373] font-normal">-</div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  },
);
ItemSheet.displayName = "ItemSheet";

export const summarizeSubtasks = (tasks: any[]) => {
  const normalizedTasks = Array.isArray(tasks) ? tasks : [];
  return {
    normalizedTasks,
    completedITems: normalizedTasks.filter((task: any) => Boolean(task?.done))
      .length,
    allItems: normalizedTasks.length,
  };
};

export type ColumnProps = {
  status: string;
  items: Item[];
  showBoostPrompts: boolean;
  loading: boolean;
  onPriorityChange?: (id: string, priority: string) => void;
  onOpenItem: (id: string) => void;
};

export const areColumnPropsEqual = (prev: ColumnProps, next: ColumnProps) => {
  if (prev.status !== next.status) return false;
  if (prev.loading !== next.loading) return false;
  if (prev.showBoostPrompts !== next.showBoostPrompts) return false;
  if (prev.items.length !== next.items.length) return false;
  for (let i = 0; i < prev.items.length; i += 1) {
    if (prev.items[i].id !== next.items[i].id) return false;
    if (prev.items[i].status !== next.items[i].status) return false;
    if (prev.items[i].completedITems !== next.items[i].completedITems)
      return false;
    if (prev.items[i].allItems !== next.items[i].allItems) return false;
    if (prev.items[i].priority !== next.items[i].priority) return false;
    if (prev.items[i].showOriginal !== next.items[i].showOriginal) return false;
  }
  return true;
};

export const boardCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCenter(args);
};

const Column = React.memo(function Column({
  status,
  items,
  loading,
  onPriorityChange,
  onOpenItem,
  onOriginalToggle,
}: {
  status: string;
  items: Item[];
  showBoostPrompts: boolean;
  loading: boolean;
  onPriorityChange?: (id: string, priority: string) => void;
  onOpenItem: (id: string) => void;
  onOriginalToggle?: (id: string) => void;
}) {
  const { setNodeRef } = useDroppable({ id: status });
  const { active, over } = useDndContext();
  const isActiveColumn =
    Boolean(active) &&
    (over?.id === status || items.some((item) => item.id === over?.id));

  return (
    <div className="flex min-w-[285px] flex-1 basis-0 self-stretch">
      <div
        ref={setNodeRef}
        className={`flex h-full w-full min-w-0 flex-col bg-gray-100 p-1.5 transition-border duration-150 ${
          isActiveColumn
            ? "border-2 border-grey-600"
            : "border-2 border-transparent"
        }`}
      >
        <div className="flex min-h-[180px] min-w-0 flex-1 flex-col overflow-visible">
          <h3 className={`flex items-center gap-2 mb-2 text-xs font-bold`}>
            <div
              className={`w-2 h-2 rounded-full`}
              style={{ backgroundColor: statusesColors[status] }}
            ></div>
            {status}
          </h3>

          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {loading
              ? Array.from({ length: 2 }).map((_, index) => {
                  return status === "To do" ? (
                    <div
                      key={index}
                      className="flex w-full bg-white rounded-md p-2 relative mb-3"
                    >
                      <div className="flex flex-col gap-2 w-full">
                        <Skeleton className="h-4 w-[100%]" />
                        <Skeleton className="h-4 w-[75%]" />
                        <div className="flex justify-between items-center">
                          <Skeleton className="h-4 w-[25%]" />
                          <Skeleton className="h-[24px] w-[24px] rounded-full" />
                        </div>
                        <Skeleton className="h-4 w-[100%]" />
                        <Skeleton className="h-4 w-[75%]" />

                        <div className="space-y-2 flex-row w-full flex gap-2 ">
                          <Skeleton className="h-4 w-[50%]" />
                          <Skeleton className="h-4 w-[50%]" />
                        </div>
                      </div>
                    </div>
                  ) : null;
                })
              : items.map((item) => (
                  <DraggableItem
                    key={item.id}
                    item={item}
                    onPriorityChange={onPriorityChange}
                    onOpenItem={onOpenItem}
                    onOriginalToggle={onOriginalToggle}
                  />
                ))}
          </SortableContext>
        </div>
      </div>
    </div>
  );
}, areColumnPropsEqual);
Column.displayName = "Column";

export default function ClustersDnDColumn({
  INITIAL_ITEMS,
  loading,
  onStatusChange,
  onPriorityChange,
  onSubtasksChange,
  showBoostPrompts,
  onItemOpened,
}: {
  INITIAL_ITEMS: Item[];
  loading: boolean;
  showNoTasksPlaceholder?: boolean;
  showBoostPrompts: boolean;
  onStatusChange?: (
    id: string,
    status: Status,
    sortIndex?: number | null,
    statusOrderIds?: string[],
    sourceOrderIds?: string[],
    sourceStatus?: Status,
  ) => void;
  onPriorityChange?: (id: string, priority: string) => void;
  onSubtasksChange?: (id: string, tasks: any[]) => Promise<void> | void;
  onItemOpened?: (item: Item) => void;
}) {
  const [items, setItems] = useState(INITIAL_ITEMS);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string>("");
  const [savingSubtasksIds, setSavingSubtasksIds] = useState<Set<string>>(
    () => new Set(),
  );
  const itemsRef = React.useRef(items);
  const dragRef = React.useRef<{
    id: string;
    status: Status;
    index: number;
  } | null>(null);

  useEffect(() => {
    setItems(INITIAL_ITEMS);
  }, [INITIAL_ITEMS]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const updateItemStatus = React.useCallback(
    (
      id: string | undefined,
      status: Status,
      sortIndex?: number | null,
      statusOrderIds?: string[],
      sourceOrderIds?: string[],
      sourceStatus?: Status,
    ) => {
      if (!id) return;
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status, sortIndex } : item,
        ),
      );
      onStatusChange?.(
        id,
        status,
        sortIndex,
        statusOrderIds,
        sourceOrderIds,
        sourceStatus,
      );
    },
    [onStatusChange],
  );

  const updateItemPriority = React.useCallback(
    (id: string, priority: string) => {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, priority } : item)),
      );
      onPriorityChange?.(id, priority);
    },
    [onPriorityChange],
  );

  const toggleItemOriginalCopy = React.useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id && hasOriginalTaskCopy(item)
          ? { ...item, showOriginal: !item.showOriginal }
          : item,
      ),
    );
  }, []);

  const updateItemSubtasks = React.useCallback(
    (id: string, tasks: any[]) => {
      const { normalizedTasks, completedITems, allItems } =
        summarizeSubtasks(tasks);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, tasks: normalizedTasks, completedITems, allItems }
            : item,
        ),
      );
      if (!onSubtasksChange) return;
      setSavingSubtasksIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      Promise.resolve(onSubtasksChange(id, normalizedTasks))
        .catch(() => null)
        .finally(() => {
          setSavingSubtasksIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        });
    },
    [onSubtasksChange],
  );

  const activeItem = useMemo(
    () => items.find((i) => i.id === activeId) ?? null,
    [activeId, items],
  );
  const openItem = useMemo(
    () => items.find((i) => i.id === openId) ?? null,
    [items, openId],
  );
  const handleOpenItem = React.useCallback(
    (id: string) => {
      setOpenId(id);
      const selectedItem = itemsRef.current.find((item) => item.id === id);
      if (selectedItem) {
        onItemOpened?.(selectedItem);
      }
    },
    [onItemOpened],
  );
  const itemsByStatus = useMemo(() => {
    return STATUSES.reduce<Record<Status, Item[]>>(
      (acc, status) => {
        acc[status] = items.filter((item) => item.status === status);
        return acc;
      },
      {} as Record<Status, Item[]>,
    );
  }, [items]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const current = itemsRef.current.find((item) => item.id === id);
    if (current) {
      const statusItems = itemsRef.current.filter(
        (item) => item.status === current.status,
      );
      const index = statusItems.findIndex((item) => item.id === id);
      dragRef.current = { id, status: current.status, index };
    }
    setActiveId(id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    setItems((items) => {
      const activeIndex = items.findIndex((i) => i.id === active.id);
      if (activeIndex === -1) return items;

      const activeItem = items[activeIndex];
      const moveAndUpdateStatus = (targetIndex: number, nextStatus: Status) => {
        const nextItems = items.map((item, index) =>
          index === activeIndex ? { ...item, status: nextStatus } : item,
        );
        let adjustedTargetIndex = targetIndex;
        if (activeIndex < targetIndex) {
          adjustedTargetIndex -= 1;
        }
        return arrayMove(nextItems, activeIndex, adjustedTargetIndex);
      };

      if (STATUSES.includes(String(over.id))) {
        const nextStatus = String(over.id) as Status;
        if (activeItem.status === nextStatus) return items;
        const lastIndexInColumn = items.reduce((acc, item, index) => {
          if (item.status === nextStatus) return index;
          return acc;
        }, -1);
        const targetIndex =
          lastIndexInColumn === -1 ? items.length - 1 : lastIndexInColumn + 1;
        return moveAndUpdateStatus(targetIndex, nextStatus);
      }

      const overIndex = items.findIndex((i) => i.id === over.id);
      if (overIndex === -1) return items;
      const overItem = items[overIndex];
      if (activeItem.status !== overItem.status) {
        return moveAndUpdateStatus(overIndex, overItem.status);
      }
      if (activeIndex === overIndex) return items;
      return arrayMove(items, activeIndex, overIndex);
    });
  };

  const handleDragEnd = (_: DragEndEvent) => {
    const dragState = dragRef.current;
    if (dragState) {
      const current = itemsRef.current.find((item) => item.id === dragState.id);
      if (current) {
        const statusItems = itemsRef.current.filter(
          (item) => item.status === current.status,
        );
        const index = statusItems.findIndex((item) => item.id === dragState.id);
        const statusChanged = current.status !== dragState.status;
        const indexChanged = index !== dragState.index;
        if (statusChanged || indexChanged) {
          const statusOrderIds = statusItems.map((item) => item.id);
          const sourceOrderIds = statusChanged
            ? itemsRef.current
                .filter((item) => item.status === dragState.status)
                .map((item) => item.id)
            : undefined;
          onStatusChange?.(
            dragState.id,
            current.status,
            index,
            statusOrderIds,
            sourceOrderIds,
            dragState.status,
          );
        }
      }
    }
    dragRef.current = null;
    setActiveId(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // Wait for 5px movement before drag
      },
    }),
  );

  return (
    <DndContext
      collisionDetection={boardCollisionDetection}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex min-h-full w-full min-w-0 max-w-full items-stretch gap-2">
        {STATUSES.map((status) => (
          <Column
            key={status}
            status={status}
            showBoostPrompts={showBoostPrompts}
            loading={loading}
            items={itemsByStatus[status]}
            onPriorityChange={updateItemPriority}
            onOpenItem={handleOpenItem}
            onOriginalToggle={toggleItemOriginalCopy}
          />
        ))}
      </div>

      <ItemSheet
        item={openItem}
        showBoostPrompts={showBoostPrompts}
        open={Boolean(openId)}
        setOpen={(isOpened) => {
          if (!isOpened) setOpenId("");
        }}
        onStatusChange={updateItemStatus}
        onPriorityChange={updateItemPriority}
        onSubtasksChange={updateItemSubtasks}
        onOriginalToggle={toggleItemOriginalCopy}
        isSubtasksSaving={
          openId ? savingSubtasksIds.has(openId) || loading : loading
        }
      ></ItemSheet>

      <DragOverlay>
        {activeItem ? (
          <Item
            item={activeItem}
            isOverlay
            onPriorityChange={updateItemPriority}
            onOriginalToggle={toggleItemOriginalCopy}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
